import dayjs from 'dayjs'
import {
  AreasByPref,
  GroupedAreaList,
  SearchHistory,
  Area,
  FacilityId,
} from './types'
import { Activity } from '../Activities'
import {
  sexLimitationOptions,
  exerciseLevelOptions,
  otherConditionOptions,
  EXCLUDE_FULL_RESERVATIONS_KEY,
  initialSearchParams,
} from './constants'
import { getPrefectureSlugById } from '../Prefectures'
import { PrefectureId } from '@/externals/gyym/user/prefectures'
import { ProgramId } from '@/externals/gyym/user/programs'

export const groupAreaByPrefecture = async (
  areasByPref: AreasByPref[]
): Promise<GroupedAreaList> => {
  return areasByPref.reduce(
    (acc, { id, name, areas }) => ({
      ...acc,
      [id]: {
        name,
        areas,
        totalFacilityCount: areas.reduce((acc, { count }) => acc + count, 0),
      },
    }),
    {}
  )
}

/**
 * 検索履歴をユーザ向けの文字列に変換する
 */
export const searchHistoryToReadableString = (
  history: SearchHistory,
  areas: Area[],
  activities: Activity[]
): string => {
  const textArr = [
    // 施設名検索
    history.facilityName ? history.facilityName : null,
    // エリア
    history.areaIds.length !== 0
      ? history.areaIds
          .map((id) => areas.find((area) => area.id === id)?.name)
          .filter((item) => item)
          .join(',')
      : null,
    // 日付
    history.dates.length !== 0
      ? history.dates.map((date) => dayjs(date).format('M月D日')).join(',')
      : null,
    // 開始時間
    history.startTimeFrom || history.startTimeTo
      ? `${history.startTimeFrom || ''}~${history.startTimeTo || ''}`
      : null,
    // アクティビティ
    history.activityIds.length !== 0
      ? history.activityIds
          .map((id) => activities.find((activity) => activity.id === id)?.name)
          .filter((item) => item)
          .join(',')
      : null,
    // 価格帯
    history.priceFrom || history.priceTo
      ? `${
          history.priceFrom === null ? '指定なし' : `${history.priceFrom}pt`
        }〜${history.priceTo === null ? '指定なし' : `${history.priceTo}pt`}`
      : null,
    // 専用プログラム
    history.sexLimitations.length !== 0
      ? history.sexLimitations
          .map(
            (g) => sexLimitationOptions.find(({ value }) => value === g)?.label
          )
          .filter((item) => item)
          .join(',')
      : null,
    // 運動レベル
    history.exerciseLevels.length !== 0
      ? history.exerciseLevels
          .map(
            (g) => exerciseLevelOptions.find(({ value }) => value === g)?.label
          )
          .filter((item) => item)
          .join(',')
      : null,
    // その他
    history.excludeFullReservations
      ? otherConditionOptions.find(
          ({ value }) => value === EXCLUDE_FULL_RESERVATIONS_KEY
        )?.label
      : null,
  ]
  return textArr.filter((item) => item).join(',')
}

export const queryToSearchHistory = (query: {
  [k: string]: any
}): SearchHistory => {
  const q: { [k: string]: any } = Object.keys(initialSearchParams).reduce(
    (acc, key) => ({
      ...acc,
      [key]: query[key] || (initialSearchParams as any)[key],
    }),
    {}
  )

  if (q.areaIds && Array.isArray(q.areaIds)) {
    q.areaIds = q.areaIds.map((n: string) => Number(n))
  }

  if (q.activityIds && Array.isArray(q.activityIds)) {
    q.activityIds = q.activityIds.map((n: string) => Number(n))
  }

  if (q.priceFrom) {
    q.priceFrom = Number(q.priceFrom)
  }

  if (q.priceTo) {
    q.priceTo = Number(q.priceTo)
  }

  if (q.exerciseLevels && Array.isArray(q.exerciseLevels)) {
    q.exerciseLevels = q.exerciseLevels.map((n: string) => Number(n))
  }

  return {
    ...initialSearchParams,
    ...q,
  }
}

export const buildFacilityPath = ({
  id,
  cityCode,
  prefectureId,
}: {
  id: FacilityId
  cityCode?: number
  prefectureId?: PrefectureId
}) =>
  cityCode && prefectureId
    ? `/facilities/${getPrefectureSlugById(prefectureId)}/C${cityCode}/${id}`
    : `/facilities/${id}`

export const buildProgramPath = (
  facility: {
    id: FacilityId
    cityCode?: number
    prefectureId?: PrefectureId
  },
  program: { id: ProgramId }
) => `${buildFacilityPath(facility)}/programs/${program.id}`
